import { Grid } from "@material-ui/core";
import PropTypes from "prop-types";
import React, { useCallback, useContext, useEffect, useRef } from "react";
import TagManager from "react-gtm-module";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteAnswer,
  setAnswer,
  setUserAnswers,
} from "../../onboarding/actions";
import { Answer } from "../answer/answer";
import { gaID } from "../config";
import { getData, saveData } from "../storage";
import { WizardContext } from "../wizard/context";
import { setDisableNextControl } from "../wizard/reducer/actions";
import { multipleChoicesStyles } from "./multipleChoices.styles";

export const MultipleChoices = React.memo(
  ({ answers, step, progress, disabledCriteria, renderImage, answerClass }) => {
    const { dispatch: dispatchWizard, setPercent: setWizardPercent } =
      useContext(WizardContext);

    const classes = multipleChoicesStyles();
    const answersSelected = useRef(0);
    const dispatch = useDispatch();
    const { onboarding } = useSelector((state) => ({
      onboarding: state.onboarding.onboarding,
    }));

    useEffect(() => {
      saveData("STEP", step);

      const dataStep = getData(step);
      if (dataStep) {
        dispatch(setUserAnswers(step, JSON.parse(dataStep)));
      }

      setWizardPercent(progress);
    }, []);

    useEffect(() => {
      const totalSelectedAnswers = onboarding[step].answers.length;

      answersSelected.current = totalSelectedAnswers;

      const isDisabled = Boolean(totalSelectedAnswers <= 0);
      dispatchWizard(setDisableNextControl(isDisabled));

      saveData(step, onboarding[step].answers);
    }, [onboarding[step].answers, onboarding.quizAnswers[step]]);

    const isSelected = useCallback(
      (answerId) => {
        return onboarding[step].answers.find(
          (answer) => Number(answer.id) === Number(answerId)
        );
      },
      [onboarding[step].answers]
    );

    const onClick = useCallback(
      (answer) => {
        const { id, text } = answer;
        if (isSelected(id)) return;

        if (onboarding[step].answers.length > 0 && !isSelected(id)) {
          dispatch(deleteAnswer(step, onboarding[step].answers[0].id));
        }

        dispatch(setAnswer(step, { id, text }));

        if (gaID) {
          // Google Analytics 4 custom event tracking.
          TagManager.dataLayer({
            dataLayer: {
              js: new Date(),
              event: "want_to_achieve",
              answer: answer?.text ?? null,
            },
          });
        }
      },
      [onboarding[step].answers]
    );

    return (
      <Grid container className={classes.container} spacing={2}>
        {answers.map((answer) => (
          <Answer
            answerClass={answerClass}
            renderImage={renderImage}
            key={answer.focusId}
            answer={{
              order: answer.focusId,
              id: answer.focusId,
              text: answer.name,
              disabledSelection: !!disabledCriteria(answer),
            }}
            isAnswerSelected={!!isSelected(answer.focusId)}
            onClick={onClick}
          />
        ))}
      </Grid>
    );
  }
);

MultipleChoices.propTypes = {
  answers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
    })
  ).isRequired,
  step: PropTypes.string.isRequired,
  progress: PropTypes.number,
  disabledCriteria: PropTypes.func,
  renderImage: PropTypes.func,
  answerClass: PropTypes.string,
};

MultipleChoices.defaultProps = {
  progress: 0,
  disabledCriteria: () => false,
  renderImage: undefined,
  answerClass: "",
};
